import { computed, Injectable, signal } from '@angular/core';

const SESSION_KEY_TOKEN = 'fp_token';
const SESSION_KEY_VOX_ORDER_ID = 'fp_voxOrderId';
const SESSION_KEY_FAILURE_COUNT = 'fp_failureCount';
const SESSION_KEY_LOAN_ID = 'fp_loanId';
const SESSION_KEY_BORROWER_ID = 'fp_borrowerId';

@Injectable({ providedIn: 'root' })
export class FrontPorchStateService {
  readonly token = signal<string | null>(null);
  readonly loanId = signal<string | null>(null);
  readonly borrowerId = signal<string | null>(null);
  readonly voxOrderId = signal<string | null>(null);
  readonly invitationId = signal<string | null>(null);
  readonly userName = signal('');
  readonly userEmail = signal('');
  readonly orderGuid = signal<string | null>(null);

  private readonly MAX_RETRIES = 3;
  readonly failureCount = signal(0);
  readonly requiresAssistance = computed(() => this.failureCount() >= this.MAX_RETRIES);

  initFromQueryParams(params: Record<string, string>): void {
    const previousToken = sessionStorage.getItem(SESSION_KEY_TOKEN);
    const newToken = params['token'] ?? null;
    const isNewSession = !previousToken || newToken !== previousToken;

    this.token.set(null);
    this.loanId.set(null);
    this.borrowerId.set(null);
    this.voxOrderId.set(null);
    this.invitationId.set(null);
    this.userName.set('');
    this.userEmail.set('');
    this.orderGuid.set(null);
    this.failureCount.set(0);

    if (isNewSession) {
      sessionStorage.removeItem(SESSION_KEY_FAILURE_COUNT);
      this.clearPersistedState();
    }

    if (params['token']) this.token.set(params['token']);
    if (params['loanNumber']) this.loanId.set(params['loanNumber']);
    if (params['userName']) this.userName.set((params['userName'] ?? '').replace(/\+/g, ' '));
    if (params['userEmail']) this.userEmail.set(params['userEmail']);
    if (params['invitationGuid']) this.invitationId.set(params['invitationGuid']);
    if (params['voxOrderId']) this.voxOrderId.set(params['voxOrderId']);

    this.restoreVoxOrderId();
    this.restoreFailureCount();
    this.restoreLoanAndBorrower();

    if (newToken) {
      sessionStorage.setItem(SESSION_KEY_TOKEN, newToken);
    }
  }

  setBorrowerIdAndLoanId(borrowerId: string, loanId: string): void {
    this.borrowerId.set(borrowerId);
    this.loanId.set(loanId);
    this.persistLoanAndBorrower();
  }

  recordFailure(): void {
    this.failureCount.update((count) => count + 1);
    this.persistFailureCount();
  }

  clearFailures(): void {
    this.failureCount.set(0);
    sessionStorage.removeItem(SESSION_KEY_FAILURE_COUNT);
  }

  persistVoxOrderId(): void {
    const id = this.voxOrderId();
    if (id) {
      sessionStorage.setItem(SESSION_KEY_VOX_ORDER_ID, id);
    }
  }

  restoreVoxOrderId(): void {
    if (!this.voxOrderId()) {
      const stored = sessionStorage.getItem(SESSION_KEY_VOX_ORDER_ID);
      if (stored) this.voxOrderId.set(stored);
    }
  }

  private persistFailureCount(): void {
    sessionStorage.setItem(SESSION_KEY_FAILURE_COUNT, String(this.failureCount()));
  }

  private restoreFailureCount(): void {
    const stored = sessionStorage.getItem(SESSION_KEY_FAILURE_COUNT);
    if (stored) {
      const count = parseInt(stored, 10);
      if (!isNaN(count)) this.failureCount.set(count);
    }
  }

  private clearPersistedState(): void {
    sessionStorage.removeItem(SESSION_KEY_TOKEN);
    sessionStorage.removeItem(SESSION_KEY_VOX_ORDER_ID);
    sessionStorage.removeItem(SESSION_KEY_LOAN_ID);
    sessionStorage.removeItem(SESSION_KEY_BORROWER_ID);
  }

  private persistLoanAndBorrower(): void {
    const loan = this.loanId();
    const borrower = this.borrowerId();
    if (loan) sessionStorage.setItem(SESSION_KEY_LOAN_ID, loan);
    if (borrower) sessionStorage.setItem(SESSION_KEY_BORROWER_ID, borrower);
  }

  private restoreLoanAndBorrower(): void {
    if (!this.loanId()) {
      const stored = sessionStorage.getItem(SESSION_KEY_LOAN_ID);
      if (stored) this.loanId.set(stored);
    }
    if (!this.borrowerId()) {
      const stored = sessionStorage.getItem(SESSION_KEY_BORROWER_ID);
      if (stored) this.borrowerId.set(stored);
    }
  }
}
